<template>
  <div v-if="countryCode && (normalizedPartnerId || configureOnly)">
    <div v-if="loading" class="text-muted mb-3"><i class="fas fa-spinner fa-spin"></i> Loading exceptions...</div>

    <div class="mb-4">
      <h6>By currency</h6>
      <b-table v-if="currencyItems.length" :small="true" :striped="true" :items="currencyTableItems"
        :fields="currencyFields">
        <template #cell(actions)="row">
          <b-button variant="outline-danger" size="xs" @click="removeCurrencyAt(row.index)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>
      <p v-else-if="!loading" class="text-muted small mb-2">No currency exceptions</p>
      <multiselect v-model="pickerCurrency" :options="availableCurrencyOptions" label="label" track-by="value"
        placeholder="Select currency" style="max-width: 320px" @select="onCurrencySelect" />
    </div>

    <div class="mb-4">
      <h6>By game type</h6>
      <b-table v-if="gameTypeItems.length" :small="true" :striped="true" :items="gameTypeTableItems"
        :fields="gameTypeFields">
        <template #cell(actions)="row">
          <b-button variant="outline-danger" size="xs" @click="removeGameTypeAt(row.index)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>
      <p v-else-if="!loading" class="text-muted small mb-2">No game type exceptions</p>
      <multiselect v-model="pickerGameType" :options="availableGameTypeOptions" :custom-label="gameTypeOptionLabel"
        track-by="id" :internal-search="true" placeholder="Search by game type ID or name"
        style="max-width: 320px" @select="onGameTypeSelect" />
    </div>

    <div class="mb-2">
      <h6>By table</h6>
      <b-table v-if="tableItems.length" :small="true" :striped="true" :items="tableTableItems" :fields="tableFields">
        <template #cell(actions)="row">
          <b-button variant="outline-danger" size="xs" @click="removeTableAt(row.index)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>
      <p v-else-if="!loading" class="text-muted small mb-2">No table exceptions</p>
      <multiselect v-model="pickerTable" :options="availableTableOptions" :custom-label="tableOptionLabel"
        track-by="value" :internal-search="true" placeholder="Search by table ID or name"
        style="max-width: 320px" @select="onTableSelect" />
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import {
  fetchCountryExceptions,
  hasExceptionItems,
  normalizePartnerId,
} from "./exceptionUtils";
import {
  buildGameTypeOptionsForSelect,
  formatGameTypeDisplay,
  gameTypeOptionLabel,
  getDisplayGameTypeId,
  toStorageGameType,
} from "./gameTypeUtils";

function cloneItems(items) {
  return (items || []).map(item => ({ ...item }));
}

export default {
  name: "CountryExceptionEditor",
  components: { Multiselect },
  props: {
    countryCode: { type: String, default: null },
    partnerId: { type: String, default: null },
    configureOnly: { type: Boolean, default: false },
    initialExceptions: { type: Object, default: null },
  },
  data() {
    return {
      gameTypeOptions: [],
      tableOptions: [],
      currencyOptions: [],
      currencyItems: [],
      gameTypeItems: [],
      tableItems: [],
      pickerCurrency: null,
      pickerGameType: null,
      pickerTable: null,
      loading: false,
      loadedBaseline: null,
      currencyFields: [
        { key: 'currencyDisplay', label: 'Currency' },
        { key: 'actions', label: '' },
      ],
      gameTypeFields: [
        { key: 'gameTypeDisplay', label: 'Game Type' },
        { key: 'actions', label: '' },
      ],
      tableFields: [
        { key: 'tableDisplay', label: 'Table' },
        { key: 'actions', label: '' },
      ],
    };
  },
  computed: {
    normalizedPartnerId() {
      return normalizePartnerId(this.partnerId);
    },
    currencyTableItems() {
      return this.currencyItems.map(i => ({ ...i, currencyDisplay: i.currency ? i.currency : 'ALL currencies' }));
    },
    gameTypeTableItems() {
      return this.gameTypeItems.map(i => ({
        ...i,
        gameTypeDisplay: formatGameTypeDisplay(this.gameTypeOptions, i),
      }));
    },
    tableTableItems() {
      return this.tableItems.map(i => ({
        ...i,
        tableDisplay: (this.tableOptions.find(t => Number(t.value) === Number(i.tableId))?.label ?? i.tableId)
          + ` (#${i.tableId})`,
      }));
    },
    loadKey() {
      return `${this.countryCode || ''}|${this.normalizedPartnerId || ''}`;
    },
    availableCurrencyOptions() {
      const used = new Set(this.currencyItems.map(i => i.currency || null));
      return this.currencyOptions.filter(o => !used.has(o.value ? o.value.toUpperCase() : null));
    },
    availableGameTypeOptions() {
      const used = new Set(this.gameTypeItems.map(i => getDisplayGameTypeId(i)));
      return this.gameTypeOptions.filter(o => !used.has(Number(o.id)));
    },
    availableTableOptions() {
      const used = new Set(this.tableItems.map(i => Number(i.tableId)));
      return this.tableOptions.filter(o => !used.has(Number(o.value)));
    },
  },
  watch: {
    loadKey: { handler() { this.reload(); }, immediate: true },
    initialExceptions: {
      handler(value) {
        if (hasExceptionItems(value)) {
          this.applyExceptions(value);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    captureLoadedBaseline() {
      this.loadedBaseline = this.getSnapshot();
    },
    getLoadedBaseline() {
      return this.loadedBaseline || { currency: [], gameType: [], table: [] };
    },
    applyExceptions(data) {
      if (!data) return;
      this.currencyItems = cloneItems(data.currency);
      this.gameTypeItems = cloneItems(data.gameType);
      this.tableItems = cloneItems(data.table);
    },
    reload() {
      this.pickerCurrency = null;
      this.pickerGameType = null;
      this.pickerTable = null;
      this.loadedBaseline = null;

      if (hasExceptionItems(this.initialExceptions)) {
        this.applyExceptions(this.initialExceptions);
        this.captureLoadedBaseline();
      } else {
        this.currencyItems = [];
        this.gameTypeItems = [];
        this.tableItems = [];
      }

      if (this.countryCode && this.normalizedPartnerId && !this.configureOnly) {
        this.loadExceptions();
      } else {
        this.captureLoadedBaseline();
      }
    },
    tableOptionLabel(option) {
      if (!option) return '';
      return `${option.value} - ${option.label}`;
    },
    gameTypeOptionLabel,
    async loadOptions() {
      await Promise.all([
        this.$store.dispatch('gameTypes/loadList'),
        this.$store.dispatch('tablesForSelect/loadList'),
        this.$store.dispatch('currenciesForSelect/loadList'),
      ]);
      this.gameTypeOptions = buildGameTypeOptionsForSelect(this.$store.getters['gameTypes/list']);
      this.tableOptions = this.$store.getters['tablesForSelect/list'];
      this.currencyOptions = [
        { label: 'ALL currencies', value: null },
        ...this.$store.getters['currenciesForSelect/list'],
      ];
    },
    async loadExceptions() {
      this.loading = true;
      try {
        const data = await fetchCountryExceptions(
          (config) => this.$http(config),
          this.countryCode,
          this.normalizedPartnerId,
        );
        if (hasExceptionItems(data)) {
          this.applyExceptions(data);
        } else {
          this.currencyItems = [];
          this.gameTypeItems = [];
          this.tableItems = [];
        }
        this.captureLoadedBaseline();
      } catch (err) {
        this.$toastr.e('Failed to load exceptions');
      } finally {
        this.loading = false;
      }
    },
    onCurrencySelect(option) {
      const currency = option.value ? option.value.toUpperCase() : null;
      this.currencyItems.push({
        id: null,
        countryCode: this.countryCode,
        currency,
        partnerId: this.normalizedPartnerId,
      });
      this.pickerCurrency = null;
    },
    onGameTypeSelect(option) {
      const { gameType, algGameType } = toStorageGameType(option.id);
      this.gameTypeItems.push({
        id: null,
        countryCode: this.countryCode,
        gameType,
        algGameType,
        partnerId: this.normalizedPartnerId,
      });
      this.pickerGameType = null;
    },
    onTableSelect(option) {
      this.tableItems.push({
        id: null,
        countryCode: this.countryCode,
        tableId: Number(option.value),
        partnerId: this.normalizedPartnerId,
      });
      this.pickerTable = null;
    },
    removeCurrencyAt(index) {
      this.currencyItems.splice(index, 1);
    },
    removeGameTypeAt(index) {
      this.gameTypeItems.splice(index, 1);
    },
    removeTableAt(index) {
      this.tableItems.splice(index, 1);
    },
    async saveAll(originalItems, options = {}) {
      const partnerId = normalizePartnerId(options.partnerId || this.normalizedPartnerId);
      if (!partnerId) {
        this.$toastr.e('Operator is required to save exceptions');
        throw new Error('partnerId is required');
      }

      const currentItems = options.items || this.getSnapshot();
      const original = originalItems || this.getLoadedBaseline();
      const headers = { 'Content-Type': 'application/json' };

      const deletes = [
        ...original.currency.filter(o => !currentItems.currency.some(i => i.id && i.id === o.id))
          .map(i => ['country-except', i.id]),
        ...original.gameType.filter(o => !currentItems.gameType.some(i => i.id && i.id === o.id))
          .map(i => ['country-except-gametype', i.id]),
        ...original.table.filter(o => !currentItems.table.some(i => i.id && i.id === o.id))
          .map(i => ['country-except-table-id', i.id]),
      ];

      const creates = [
        ...currentItems.currency.filter(i => !i.id).map(i => ['country-except', {
          countryCode: this.countryCode,
          currency: i.currency,
          partnerId,
        }]),
        ...currentItems.gameType.filter(i => !i.id).map(i => ['country-except-gametype', {
          countryCode: this.countryCode,
          gameType: Number(i.gameType),
          algGameType: i.algGameType !== null && i.algGameType !== undefined ? Number(i.algGameType) : null,
          partnerId,
        }]),
        ...currentItems.table.filter(i => !i.id).map(i => ['country-except-table-id', {
          countryCode: this.countryCode,
          tableId: Number(i.tableId),
          partnerId,
        }]),
      ];

      for (const [resource, id] of deletes) {
        await this.$http({ url: `/api/v1/${resource}/${id}`, method: 'DELETE' });
      }

      for (const [resource, data] of creates) {
        await this.$http({ url: `/api/v1/${resource}`, method: 'POST', data, headers });
      }

      if (!options.partnerId) {
        await this.loadExceptions();
        this.captureLoadedBaseline();
      }
    },
    getSnapshot() {
      return {
        currency: cloneItems(this.currencyItems),
        gameType: cloneItems(this.gameTypeItems),
        table: cloneItems(this.tableItems),
      };
    },
  },
  mounted() {
    this.loadOptions();
  },
};
</script>
